import { OwnerProjectsPage } from '../owner/ProjectsPage';
import React, { useEffect, useState } from 'react';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ExternalLink, Link2, Mail } from 'lucide-react';
import { toast } from 'react-toastify';
import clsx from 'clsx';
import { aaiApi } from '../../services/aaiApi';
import { getProviderClientInviteLink, requestAgentBridgeUrl, sendProviderClientInvite } from '../../services/api';
import { useAsync } from '../aai/useAsync';
import type { PartnerClientRow, PartnerOverview } from '../../types/aai';
import {
  Action, Chip, EmptyState, ErrorNote, Loading, PageBody, Panel, RouteMark, StatusChip,
} from '../../components/aa';
import { OrbitRoomPanel } from '../orbit-room/OrbitRoomPanel';
import { ProjectBudgetsPanel } from '../../components/settings/ProjectBudgetsPanel';
// Documents and Download Orbit hidden for provider owners for now.
// import { DocumentLibrary } from '../partner/DocumentLibrary';
// import { DownloadOrbit } from '../partner/DownloadOrbit';
import { STATUS_CHIP, STATUS_LABEL } from '../partner/format';

/*
 * One client, as its provider's Owner sees it: the client's dashboard the way
 * a Solution Partner reads it, its projects, its documents, and the door into
 * its builder workspace. Every read below is scoped to the client; the shell
 * around it keeps reading the Owner's own company.
 */

type Tab = 'overview' | 'projects' | 'budgets';

const TABS: { id: Tab; label: string }[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'projects', label: 'Projects' },
  { id: 'budgets', label: 'Project budgets' },
  // { id: 'documents', label: 'Documents' },
];

const openWorkspace = async (clientId: string, projectId?: string) => {
  try {
    const res = await requestAgentBridgeUrl(undefined, clientId, projectId);
    if (res?.url) window.open(res.url, '_blank');
  } catch (e: any) {
    toast.error(e?.response?.data?.detail || 'Could not open the workspace');
  }
};

const ClientHead: React.FC<{ row: PartnerClientRow; retainer: boolean }> = ({ row, retainer }) => {
  const [busy, setBusy] = useState(false);

  const copyLink = async () => {
    try {
      const res = await getProviderClientInviteLink(row.id);
      await navigator.clipboard.writeText(res.url);
      toast.success('Invite link copied');
    } catch {
      toast.error('Could not copy the invite link');
    }
  };

  const sendInvite = async () => {
    setBusy(true);
    try {
      const res = await sendProviderClientInvite(row.id);
      toast.success(`Invite sent to ${res.email}`);
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Could not send the invite');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-6">
      <div className="flex min-w-0 items-center gap-[18px]">
        <RouteMark glyph={row.initials} size="xl" className="bg-ink text-white" />
        <div className="min-w-0">
          <div className="flex items-center gap-2.5">
            <h1 className="m-0 text-[26px] font-bold leading-[33px] tracking-[-0.015em] text-ink">{row.name}</h1>
            <StatusChip status={STATUS_CHIP[row.status]} label={STATUS_LABEL[row.status]} />
            {retainer && <Chip tone="private">Retainer</Chip>}
          </div>
          <p className="m-0 mt-1 text-[13px] text-ink-muted">{row.onboarding_label}</p>
        </div>
      </div>
      <div className="flex shrink-0 flex-wrap items-center gap-2">
        {row.status !== 'active' && (
          <>
            <Action variant="quiet" icon={<Link2 size={14} />} onClick={copyLink}>Copy invite link</Action>
            <Action variant="quiet" icon={<Mail size={14} />} onClick={sendInvite} disabled={busy} reason="Sending the invite">
              {row.invite_issued_at ? 'Resend invite' : 'Send invite'}
            </Action>
          </>
        )}
        {/* <DownloadOrbit clientId={row.id} clientName={row.name} /> */}
        <Action icon={<ExternalLink size={14} />} onClick={() => openWorkspace(row.id)}>Open workspace</Action>
      </div>
    </div>
  );
};

const ProviderClientPage: React.FC = () => {
  const { clientId = '' } = useParams();
  const [params] = useSearchParams();
  const [retainer, setRetainer] = useState<boolean | null>(null);
  const [tab, setTab] = useState<Tab>(TABS.find((t) => t.id === params.get('tab'))?.id || 'overview');
  const overview = useAsync<PartnerOverview>(() => aaiApi.getPartnerOverview(), 'Could not load this client.', [clientId]);

  useEffect(() => () => aaiApi.setAaiCompany(null), []);

  if (!localStorage.getItem('provider_id')) return <Navigate to="/dashboard" replace />;
  if (overview.loading) return <PageBody><Loading label="Loading the client" /></PageBody>;
  if (overview.error) return <PageBody><ErrorNote message={overview.error} /></PageBody>;

  const row = overview.data?.clients.find((c) => c.id === clientId) || null;
  if (!row) {
    return (
      <PageBody>
        <Panel>
          <EmptyState title="Client not found" reason="This company is not one of your clients." action={<Action to="/clients">Back to clients</Action>} />
        </Panel>
      </PageBody>
    );
  }

  // Set during render, not in an effect: a child's fetch effect runs before
  // this component's, so an effect here would let the first read go out
  // unscoped and answer for the Owner's own company.
  aaiApi.setAaiCompany(row.id);

  return (
    <PageBody>
      <Action variant="ghost" size="sm" icon={<ArrowLeft size={14} />} to="/clients" className="mb-4">All clients</Action>
      <ClientHead row={row} retainer={retainer ?? !!row.retainer} />
      <nav aria-label="Client pages" className="mb-5 flex flex-wrap gap-0.5 border-b border-line pb-2">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            aria-current={tab === t.id ? 'page' : undefined}
            className={clsx(
              'rounded-lg px-3 py-2 text-[13px] font-semibold transition-colors',
              tab === t.id && 'bg-canvas-inset text-ink',
              tab !== t.id && 'text-ink-muted hover:bg-canvas-inset hover:text-ink',
            )}
          >
            {t.label}
          </button>
        ))}
      </nav>
      {tab === 'overview' && <OrbitRoomPanel key={row.id} viewer="partner" clientId={row.id} clientName={row.name} layout="page" />}
      {tab === 'projects' && <OwnerProjectsPage key={row.id} embedded clientId={row.id} />}
      {tab === 'budgets' && <ProjectBudgetsPanel key={row.id} side={{ kind: 'provider', clientId: row.id }} clientName={row.name} onRetainer={setRetainer} />}
      {/* Documents hidden for now.
      {tab === 'documents' && (
        <DocumentLibrary
          key={row.id}
          head={{
            kicker: 'Client documents',
            title: `Documents for ${row.name}`,
            desc: 'The files this client works from. What you add for the whole company or a department, its owner sees too.',
          }}
        />
      )}
      */}
    </PageBody>
  );
};

export default ProviderClientPage;
